import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, Trash2, Save, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/pages/admin/AdminPeople";
import { api, errMsg } from "@/lib/api";

function FileButton({ label, accept, onFile, testId }) {
  return (
    <label className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-full border border-slate-300 bg-white hover:border-teal-400 cursor-pointer" data-testid={testId}>
      <Upload className="h-4 w-4" />{label}
      <input type="file" accept={accept} className="sr-only" onChange={(e) => e.target.files[0] && onFile(e.target.files[0])} />
    </label>
  );
}

async function upload(url, file, msg) {
  const fd = new FormData(); fd.append("file", file);
  const t = toast.loading("Uploading…");
  try { await api.post(url, fd); toast.success(msg, { id: t }); return true; } catch (e) { toast.error(errMsg(e), { id: t }); return false; }
}

const RUNNING = ["queued", "merging", "captioning", "uploading"];

function HeygenImport({ m, reload }) {
  const [links, setLinks] = useState("");
  const st = m.media_import?.status;
  useEffect(() => {
    if (!RUNNING.includes(st)) return;
    const id = setInterval(reload, 5000);
    return () => clearInterval(id);
  }, [st, reload]);
  const start = async () => {
    try {
      await api.post(`/admin/modules/${m.slug}/import-heygen`, { links: links.split(/\s+/).filter(Boolean) });
      toast.success("Import started. Merging scenes, adding captions and uploading usually takes 2 to 5 minutes."); setLinks(""); reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-2 border-t border-slate-200 pt-3" data-testid={`heygen-import-${m.slug}`}>
      <p className="text-xs font-semibold text-slate-700">Import from HeyGen (paste scene links in order, one per line). Scenes are merged into one video with captions and a transcript.</p>
      <Textarea rows={3} value={links} onChange={(e) => setLinks(e.target.value)} placeholder={"https://app.heygen.com/videos/scene-1-...\nhttps://app.heygen.com/videos/scene-2-..."} className="text-xs bg-white" data-testid="heygen-import-links" />
      <div className="flex items-center gap-3">
        <Button size="sm" onClick={start} disabled={!links.trim() || RUNNING.includes(st)} data-testid="heygen-import-start">{RUNNING.includes(st) ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}Merge & import</Button>
        {st && <span className={`text-xs ${st === "failed" ? "text-rose-600" : "text-slate-600"}`} data-testid="heygen-import-status">Import: {st}{m.media_import?.size_mb ? ` · ${m.media_import.size_mb} MB` : ""}{m.media_import?.error ? ` — ${m.media_import.error}` : ""}</span>}
      </div>
    </div>
  );
}

export function MediaControls({ m, reload }) {
  const [url, setUrl] = useState(m.video_url || "");
  useEffect(() => setUrl(m.video_url || ""), [m.slug, m.video_url]);
  const saveUrl = async () => { try { await api.put(`/admin/modules/${m.slug}`, { video_url: url }); toast.success("Video URL saved"); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const remove = async () => { if (window.confirm("Remove video and captions?")) { await api.delete(`/admin/modules/${m.slug}/video`); reload(); } };
  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-600">Current: {m.video_path ? `uploaded file (${m.video_source || "upload"})` : m.video_url ? "external URL" : "none — caption preview shown"} · Captions: {m.captions_path ? "yes" : "none"}</p>
      <div className="flex flex-wrap gap-2">
        <FileButton label="Upload video (MP4)" accept="video/mp4,video/webm,video/quicktime" testId="upload-video-button" onFile={async (f) => (await upload(`/admin/modules/${m.slug}/video`, f, "Video uploaded")) && reload()} />
        <FileButton label="Upload captions (.vtt/.srt)" accept=".vtt,.srt,text/vtt" testId="upload-captions-button" onFile={async (f) => (await upload(`/admin/modules/${m.slug}/captions`, f, "Captions uploaded")) && reload()} />
        {(m.video_path || m.video_url) && <Button variant="ghost" size="sm" onClick={remove} className="text-rose-600" data-testid="remove-video-button"><Trash2 className="h-4 w-4 mr-1" />Remove</Button>}
      </div>
      <div className="flex gap-2"><Input placeholder="Or paste a hosted MP4 URL (https://…)" value={url} onChange={(e) => setUrl(e.target.value)} data-testid="video-url-input" /><Button variant="outline" onClick={saveUrl} data-testid="video-url-save">Save URL</Button></div>
      <HeygenImport m={m} reload={reload} />
    </div>
  );
}

export function ContentEditor() {
  const [data, setData] = useState(null);
  const [slug, setSlug] = useState("remote-nursing-market-2026");
  const [form, setForm] = useState({});
  const load = () => api.get("/admin/modules").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);
  const m = data?.modules.find((x) => x.slug === slug);
  useEffect(() => { if (m) setForm({ title: m.title, summary: m.summary, lesson: m.lesson, exercise: m.exercise, uk_notes: m.uk_notes }); }, [m?.slug, data]); // eslint-disable-line
  if (!data || !m) return null;
  const save = async () => { try { await api.put(`/admin/modules/${slug}`, form); toast.success("Lesson saved"); load(); } catch (e) { toast.error(errMsg(e)); } };
  const f = (k) => ({ value: form[k] || "", onChange: (e) => setForm({ ...form, [k]: e.target.value }) });
  return (
    <div className="grid lg:grid-cols-[1fr_340px] gap-6" data-testid="admin-content">
      <Card className="space-y-4">
        <Select value={slug} onValueChange={setSlug}>
          <SelectTrigger data-testid="content-module-select"><SelectValue /></SelectTrigger>
          <SelectContent>{data.modules.map((x) => <SelectItem key={x.slug} value={x.slug}>{x.number === 0 ? "Welcome video" : `Module ${x.number}: ${x.title}`}</SelectItem>)}</SelectContent>
        </Select>
        <div className="space-y-1.5"><Label>Title</Label><Input {...f("title")} data-testid="content-title-input" /></div>
        {m.number > 0 && <>
          <div className="space-y-1.5"><Label>Summary</Label><Textarea rows={2} {...f("summary")} data-testid="content-summary-input" /></div>
          <div className="space-y-1.5"><Label>Lesson text (Markdown: ## headings, - bullets, | tables |)</Label><Textarea rows={22} className="font-mono text-xs" {...f("lesson")} data-testid="content-lesson-input" /></div>
          <div className="space-y-1.5"><Label>Exercise prompt</Label><Textarea rows={3} {...f("exercise")} data-testid="content-exercise-input" /></div>
          <div className="space-y-1.5"><Label>UK notes</Label><Textarea rows={4} {...f("uk_notes")} data-testid="content-uknotes-input" /></div>
        </>}
        <Button onClick={save} className="rounded-full" data-testid="content-save-button"><Save className="h-4 w-4 mr-1.5" />Save changes</Button>
      </Card>
      <div className="space-y-6">
        <Card className="space-y-3"><h3 className="font-bold">Video for this module</h3><MediaControls m={m} reload={load} /></Card>
        <Card className="space-y-3" data-testid="admin-downloads">
          <h3 className="font-bold">Downloads (all modules)</h3>
          {data.downloads.map((d) => (
            <div key={d.id} className="border-t border-slate-100 pt-3 space-y-1.5">
              <p className="text-sm font-medium">{d.title}</p>
              <p className="text-xs text-slate-500 break-all">{d.filename}{d.storage_path ? " · replaced" : " · default"}</p>
              <FileButton label="Replace file" testId={`replace-download-${d.key}`} onFile={async (file) => (await upload(`/admin/downloads/${d.id}`, file, "Download replaced")) && load()} />
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
}
